import type { EdgeState, ForecastEntry, GridSnapshot, NodeState } from "@reagent/sim";

/**
 * Derives the on-board hazard tells from a `GridSnapshot` — spec 001's
 * "read the rig" rule: a tell appears once its counter leaves its resting
 * value (heat 0, pressure 0, wall HP at max, catalyst health at 100, residence
 * 0) and stays while it is away from it.
 *
 * Everything here is read straight off the snapshot fields spec 001's task 1
 * added — `criticalTemp`, `burstLimit`, `maxHp`, `limits.sludgeResidenceTicks`
 * — never a constant re-typed in the renderer. That is what keeps a Burst Disk
 * (or any future attachment that moves a limit) showing up here for free.
 *
 * This module is pure: snapshot in, tells out. `render/hardware/*` (task 5)
 * and the tile inspector (task 6) both consume `hazardTellsFor` and
 * `HAZARD_CONSEQUENCE`, so the board and the panel never say different things
 * about the same hazard.
 */

export type HazardKind =
  | "heat"
  | "pressure"
  | "wallHp"
  | "catalystHealth"
  | "residence"
  | "contamination"
  | "verminRisk";

export interface HazardTell {
  readonly hazard: HazardKind;
  readonly targetKind: "node" | "edge" | "tile" | "facility";
  /** `NodeState.id` or `EdgeState.id` (for "node"/"edge"), a tile position key
   * `${pos.x},${pos.y}` (for "tile"), or "facility" (for "facility"). */
  readonly targetId: string;
  /** The counter's current value, straight off the snapshot. */
  readonly value: number;
  /** Where the hazard's consequence fires, straight off the snapshot. */
  readonly limit: number;
  /** What happens at the limit, worded from docs/design/03-hazards.md. */
  readonly consequence: string;
}

/**
 * Consequence copy, one entry per hazard, shared by the on-board tell and the
 * tile inspector so they read as one voice. Names the problem and what
 * happens at the limit; never a card (CLAUDE.md's diegetic-UI rule and spec
 * 001's "hazard readouts name the problem, not the answer").
 */
export const HAZARD_CONSEQUENCE: Record<HazardKind, string> = {
  heat:
    "At its limit the machine enters Overheat Warning; left uncooled, it explodes, destroying " +
    "itself and clearing its adjacent pipes.",
  pressure:
    "At its limit the pipe bursts, spilling its packet onto the tile and shutting down flow " +
    "along that line.",
  wallHp: "At 0 HP the pipe leaks, losing 1 unit per tick and contaminating the board.",
  catalystHealth: "At 0% the bed is poisoned and reverts to standard, single-speed reaction.",
  residence: "At its limit the packet turns to sludge, dropping the node to half throughput until it's flushed.",
  contamination: "A contaminated tile locks against new hardware placement and corrodes adjacent unlined pipes by 5 HP/tick.",
  verminRisk: "At its limit rodents move into a machine, halving its working rate until it sits empty for a whole turn or is fumigated.",
};

function nodeTells(node: NodeState, sludgeResidenceTicks: number): HazardTell[] {
  const tells: HazardTell[] = [];

  if (node.heatCounter !== 0) {
    tells.push({
      hazard: "heat",
      targetKind: "node",
      targetId: node.id,
      value: node.heatCounter,
      limit: node.criticalTemp,
      consequence: HAZARD_CONSEQUENCE.heat,
    });
  }

  if (node.catalystHealth !== 100) {
    tells.push({
      hazard: "catalystHealth",
      targetKind: "node",
      targetId: node.id,
      value: node.catalystHealth,
      limit: 0,
      consequence: HAZARD_CONSEQUENCE.catalystHealth,
    });
  }

  if (node.residenceTicks !== 0) {
    tells.push({
      hazard: "residence",
      targetKind: "node",
      targetId: node.id,
      value: node.residenceTicks,
      limit: sludgeResidenceTicks,
      consequence: HAZARD_CONSEQUENCE.residence,
    });
  }

  return tells;
}

function edgeTells(edge: EdgeState): HazardTell[] {
  const tells: HazardTell[] = [];

  if (edge.pressureCounter !== 0) {
    tells.push({
      hazard: "pressure",
      targetKind: "edge",
      targetId: edge.id,
      value: edge.pressureCounter,
      limit: edge.burstLimit,
      consequence: HAZARD_CONSEQUENCE.pressure,
    });
  }

  if (edge.hp !== edge.maxHp) {
    tells.push({
      hazard: "wallHp",
      targetKind: "edge",
      targetId: edge.id,
      value: edge.hp,
      limit: 0,
      consequence: HAZARD_CONSEQUENCE.wallHp,
    });
  }

  return tells;
}

/**
 * All hazard tells implied by a snapshot, node, edge, tile, and facility alike.
 * Destroyed nodes and burst edges are omitted — there is no hardware left to
 * point a tell at. Contaminated tiles produce tells unconditionally (they are
 * hazard state, not counters approaching a limit). Vermin risk produces a tell
 * only when it is away from resting (zero), matching the existing gating pattern.
 */
export function hazardTellsFor(snapshot: GridSnapshot): readonly HazardTell[] {
  const tells: HazardTell[] = [];

  for (const node of snapshot.nodes) {
    if (node.isDestroyed) continue;
    tells.push(...nodeTells(node, snapshot.limits.sludgeResidenceTicks));
  }

  for (const edge of snapshot.edges) {
    if (edge.isBurst) continue;
    tells.push(...edgeTells(edge));
  }

  for (const tile of snapshot.contaminatedTiles) {
    tells.push({
      hazard: "contamination",
      targetKind: "tile",
      targetId: `${tile.pos.x},${tile.pos.y}`,
      value: 1,
      limit: 1,
      consequence: HAZARD_CONSEQUENCE.contamination,
    });
  }

  if (snapshot.verminRisk !== 0) {
    tells.push({
      hazard: "verminRisk",
      targetKind: "facility",
      targetId: "facility",
      value: snapshot.verminRisk,
      limit: snapshot.limits.verminRiskThreshold,
      consequence: HAZARD_CONSEQUENCE.verminRisk,
    });
  }

  return tells;
}

/**
 * Forecast badges — spec 0018's board half. A `HazardTell` says a counter is
 * away from resting *now*; a badge says the coming turn crosses a limit, and on
 * which tick. They are deliberately separate: the tell is the reading, the badge
 * is the prediction, and a node can carry one without the other (a cold rig can
 * still be forecast to burst a pipe it feeds).
 *
 * The list beside the Run switch and these badges read the same
 * `snapshot.forecast`, so the panel and the board can never disagree about what
 * is about to happen — the same rule `HAZARD_CONSEQUENCE` enforces for tells.
 */
export interface ForecastBadge {
  readonly kind: ForecastEntry["kind"];
  /** Resolved from the snapshot, so a badge always has hardware to sit on. */
  readonly targetKind: "node" | "edge";
  readonly targetId: string;
  /** 0-based tick within the coming turn. */
  readonly tickInTurn: number;
}

/**
 * Crossing copy, one entry per forecast kind. States what crosses, never what
 * to play about it — spec 0018's "a forecast is a fact, not advice".
 */
export const FORECAST_LABEL: Record<ForecastEntry["kind"], string> = {
  overheatWarning: "Overheat warning",
  explosion: "Explosion",
  pressureBurst: "Pipe burst",
  leak: "Pipe leak",
  residenceSludge: "Sludge",
  catalystPoisoned: "Catalyst poisoned",
};

/**
 * The badges implied by a snapshot's forecast: the **earliest** crossing per
 * piece of hardware, because a badge marks where to look and a node with two
 * crossings is still one place to look. The full per-kind list stays in the
 * panel, which has room for it.
 *
 * Entries whose target no longer resolves to a live node or edge are dropped
 * rather than drawn nowhere.
 */
export function forecastBadgesFor(snapshot: GridSnapshot): readonly ForecastBadge[] {
  const byTarget = new Map<string, ForecastBadge>();

  for (const entry of snapshot.forecast) {
    const targetKind = resolveForecastTarget(snapshot, entry.targetId);
    if (targetKind === null) continue;

    const existing = byTarget.get(entry.targetId);
    if (existing !== undefined && existing.tickInTurn <= entry.tickInTurn) continue;

    byTarget.set(entry.targetId, {
      kind: entry.kind,
      targetKind,
      targetId: entry.targetId,
      tickInTurn: entry.tickInTurn,
    });
  }

  return [...byTarget.values()];
}

function resolveForecastTarget(snapshot: GridSnapshot, targetId: string): "node" | "edge" | null {
  if (snapshot.nodes.some((node) => node.id === targetId && !node.isDestroyed)) return "node";
  if (snapshot.edges.some((edge) => edge.id === targetId && !edge.isBurst)) return "edge";
  return null;
}
